export interface PricingTier {
  name: string
  price: string
  description: string
  features: string[]
  highlighted: boolean
  ctaText: string
}

export interface ProductPricing {
  productId: string
  tiers: [PricingTier, PricingTier, PricingTier]
}

export const pricingData: ProductPricing[] = [
  {
    productId: 'budget-navigator',
    tiers: [
      {
        name: 'Starter',
        price: '€29/mo',
        description: 'Perfect for small businesses just getting started.',
        features: [
          'Up to 3 users',
          'Basic budget tracking',
          'Monthly reports',
          'Email support',
          '1 year data history',
          'Standard security',
        ],
        highlighted: false,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Growth',
        price: '€79/mo',
        description: 'For growing teams needing more control.',
        features: [
          'Up to 10 users',
          'Advanced forecasting',
          'Departmental budgets',
          'Priority email support',
          '5 years data history',
          'Xero/QuickBooks integration',
        ],
        highlighted: true,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'For large organizations with complex needs.',
        features: [
          'Unlimited users',
          'Custom API access',
          'Dedicated account manager',
          '24/7 phone support',
          'Unlimited history',
          'SSO & Advanced security',
        ],
        highlighted: false,
        ctaText: 'Contact Sales',
      },
    ],
  },
  {
    productId: 'cooler-pulse',
    tiers: [
      {
        name: 'Starter',
        price: '€49/mo',
        description: 'Essential HR insights for small teams.',
        features: [
          'Up to 50 employees',
          'Monthly pulse surveys',
          'Basic analytics',
          'Email support',
          'Standard templates',
          'Slack integration',
        ],
        highlighted: false,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Growth',
        price: '€129/mo',
        description: 'Advanced analytics for scaling companies.',
        features: [
          'Up to 200 employees',
          'Weekly pulse surveys',
          'Retention prediction',
          'Priority support',
          'Custom questions',
          'Manager dashboard',
        ],
        highlighted: true,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Full suite for large enterprises.',
        features: [
          'Unlimited employees',
          'Global benchmarking',
          'Custom implementation',
          'Dedicated success manager',
          'API access',
          'SSO & Audit logs',
        ],
        highlighted: false,
        ctaText: 'Contact Sales',
      },
    ],
  },
  {
    productId: 'brandably',
    tiers: [
      {
        name: 'Starter',
        price: '€39/mo',
        description: 'Basic monitoring for small brands.',
        features: [
          '3 keywords',
          'Social media monitoring',
          'Daily alerts',
          'Email support',
          'Basic sentiment analysis',
          '1 user seat',
        ],
        highlighted: false,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Growth',
        price: '€99/mo',
        description: 'Comprehensive coverage for growing brands.',
        features: [
          '10 keywords',
          'Review site monitoring',
          'Real-time alerts',
          'Priority support',
          'Advanced sentiment AI',
          '5 user seats',
        ],
        highlighted: true,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Global brand protection at scale.',
        features: [
          'Unlimited keywords',
          'Competitor tracking',
          'Crisis management tools',
          'Dedicated analyst',
          'Custom reporting',
          'Unlimited seats',
        ],
        highlighted: false,
        ctaText: 'Contact Sales',
      },
    ],
  },
  {
    productId: 'price-monitor',
    tiers: [
      {
        name: 'Starter',
        price: '€59/mo',
        description: 'Track key competitors effortlessly.',
        features: [
          '100 products tracked',
          '3 competitors',
          'Daily updates',
          'Email support',
          'Price history (30 days)',
          'CSV export',
        ],
        highlighted: false,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Growth',
        price: '€149/mo',
        description: 'Scale your pricing intelligence.',
        features: [
          '1,000 products tracked',
          '10 competitors',
          'Twice-daily updates',
          'Priority support',
          'Price history (1 year)',
          'API access',
        ],
        highlighted: true,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Industrial-grade market monitoring.',
        features: [
          'Unlimited products',
          'Unlimited competitors',
          'Real-time updates',
          'Dedicated engineer',
          'Custom integrations',
          'SLA guarantee',
        ],
        highlighted: false,
        ctaText: 'Contact Sales',
      },
    ],
  },
  {
    productId: 'legal-help',
    tiers: [
      {
        name: 'Starter',
        price: '€69/mo',
        description: 'Basic compliance for startups.',
        features: [
          '5 contract templates',
          'Basic e-signature',
          'Document storage (10GB)',
          'Email support',
          'Standard compliance checks',
          '1 admin user',
        ],
        highlighted: false,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Growth',
        price: '€199/mo',
        description: 'Automated legal ops for scale-ups.',
        features: [
          'Unlimited templates',
          'Advanced e-signature',
          'Document storage (1TB)',
          'Priority support',
          'GDPR automation',
          '5 admin users',
        ],
        highlighted: true,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Full legal department automation.',
        features: [
          'Custom template creation',
          'API integration',
          'Unlimited storage',
          'Dedicated legal engineer',
          'Audit trails',
          'SSO & Advanced permissions',
        ],
        highlighted: false,
        ctaText: 'Contact Sales',
      },
    ],
  },
  {
    productId: 'papershredder',
    tiers: [
      {
        name: 'Starter',
        price: '€39/mo',
        description: 'Digitize your office paperwork.',
        features: [
          '500 pages/month',
          'Basic OCR',
          'Searchable PDFs',
          'Email support',
          '30-day retention',
          'Mobile app access',
        ],
        highlighted: false,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Growth',
        price: '€119/mo',
        description: 'High-volume processing for teams.',
        features: [
          '5,000 pages/month',
          'Advanced AI extraction',
          'Table extraction',
          'Priority support',
          '1-year retention',
          'Workflow automation',
        ],
        highlighted: true,
        ctaText: 'Start Free Trial',
      },
      {
        name: 'Enterprise',
        price: 'Custom',
        description: 'Enterprise-grade document processing.',
        features: [
          'Unlimited pages',
          'Custom AI training',
          'On-premise option',
          'Dedicated support',
          'Unlimited retention',
          'ERP integrations',
        ],
        highlighted: false,
        ctaText: 'Contact Sales',
      },
    ],
  },
]
